import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import client from '../../api/client';
import {
  formatJalaliDateTime,
  formatPeriod,
  toLatinDigits,
  toPersianDigits,
} from '../../utils/jalali';

// API type -> label. The backend owns the mapping from these to on-disk
// directory names, so no path is ever constructed here.
const TYPES = [
  { key: 'SPI', label: 'SPI ماهانه' },
  { key: 'SPI-3M', label: 'SPI فصلی' },
  { key: 'CU', label: 'نیاز سرمایی' },
];

const ACTION_LABEL = { upload: 'افزودن', replace: 'جایگزینی', delete: 'حذف' };

const fmtSize = (bytes) => {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${toPersianDigits(bytes)} بایت`;
  if (bytes < 1024 * 1024) return `${toPersianDigits((bytes / 1024).toFixed(1))} کیلوبایت`;
  return `${toPersianDigits((bytes / (1024 * 1024)).toFixed(1))} مگابایت`;
};

const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

const RasterFiles = () => {
  const [files, setFiles] = useState([]);
  const [log, setLog] = useState([]);
  const [activeType, setActiveType] = useState('SPI');
  const [period, setPeriod] = useState('');
  const [status, setStatus] = useState({ busy: true, error: null, message: null });
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const [f, l] = await Promise.all([
        client.get('admin/rasters'),
        client.get('admin/rasters/log'),
      ]);
      setFiles(f.data);
      setLog(l.data);
      setStatus({ busy: false, error: null, message: null });
    } catch (error) {
      setStatus({
        busy: false,
        message: null,
        error: error?.response?.data?.detail || 'خطا در دریافت فهرست فایل‌ها',
      });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, message) => {
    setStatus((s) => ({ ...s, busy: true, error: null, message: null }));
    try {
      await fn();
      await load();
      setStatus({ busy: false, error: null, message });
    } catch (error) {
      setStatus({
        busy: false,
        message: null,
        error: error?.response?.data?.detail || 'عملیات ناموفق بود',
      });
    }
  };

  const upload = () => {
    const chosen = fileInput.current?.files?.[0];
    if (!chosen || !PERIOD_RE.test(period)) return;

    const body = new FormData();
    body.append('file', chosen);

    return run(async () => {
      await client.post(`admin/rasters/${activeType}/${period}`, body);
      if (fileInput.current) fileInput.current.value = '';
      setPeriod('');
    }, 'فایل بارگذاری شد و بلافاصله در سامانه قابل استفاده است');
  };

  const remove = (type, p) => run(
    () => client.delete(`admin/rasters/${type}/${p}`),
    'فایل حذف شد',
  );

  const shown = useMemo(
    () => files.filter((f) => f.raster_type === activeType),
    [files, activeType],
  );

  // Surfacing the newest period per type makes a stale dataset obvious at a
  // glance -- coldDemand in particular has been lagging the others.
  const newest = useMemo(() => {
    const out = {};
    TYPES.forEach(({ key }) => {
      const periods = files.filter((f) => f.raster_type === key).map((f) => f.period).sort();
      out[key] = periods.length ? periods[periods.length - 1] : null;
    });
    return out;
  }, [files]);

  const periodValid = PERIOD_RE.test(period);

  return (
    <div dir="rtl" className="text-right">
      {status.error && (
        <div className="mb-4 rounded bg-red-100 text-red-800 p-3">{status.error}</div>
      )}
      {status.message && (
        <div className="mb-4 rounded bg-green-100 text-green-800 p-3">{status.message}</div>
      )}

      {/* ---- freshness overview ------------------------------------------ */}
      <section className="mb-6 bg-white rounded-lg shadow p-4">
        <h2 className="font-semibold mb-3">آخرین دوره‌ی موجود</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          {TYPES.map(({ key, label }) => (
            <div key={key} className="border rounded p-3">
              <div className="text-gray-500">{label}</div>
              <div className="text-lg">{formatPeriod(newest[key])}</div>
              <div className="text-gray-400 text-xs">
                {toPersianDigits(files.filter((f) => f.raster_type === key).length)} فایل
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- upload ------------------------------------------------------ */}
      <section className="mb-6 bg-white rounded-lg shadow p-4">
        <h2 className="font-semibold mb-3">بارگذاری فایل جدید</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            <span className="block mb-1">نوع</span>
            <select
              className="border rounded p-2"
              value={activeType}
              onChange={(e) => setActiveType(e.target.value)}
            >
              {TYPES.map(({ key, label }) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block mb-1">دوره (سال-ماه شمسی)</span>
            <input
              className="border rounded p-2 w-32"
              placeholder="۱۴۰۵-۰۴"
              value={period}
              // Normalised on the way in, so typing Persian digits works and the
              // API still receives the ASCII form it validates against.
              onChange={(e) => setPeriod(toLatinDigits(e.target.value).trim())}
            />
          </label>
          <label className="text-sm">
            <span className="block mb-1">فایل GeoTIFF</span>
            <input type="file" accept=".tif,.tiff" ref={fileInput} className="border rounded p-2" />
          </label>
          <button
            type="button"
            disabled={status.busy || !periodValid}
            onClick={upload}
            className="bg-green-600 text-white rounded px-4 py-2 disabled:opacity-50"
          >
            بارگذاری
          </button>
        </div>
        {period && !periodValid && (
          <p className="text-red-600 text-sm mt-2">قالب دوره باید مثل ۱۴۰۵-۰۴ باشد.</p>
        )}
        <p className="text-gray-500 text-xs mt-2">
          بارگذاری فایل موجود، آن را جایگزین می‌کند. نیازی به build مجدد نیست.
        </p>
      </section>

      {/* ---- file list --------------------------------------------------- */}
      <section className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex gap-2 mb-3">
          {TYPES.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveType(key)}
              className={`px-3 py-1 rounded text-sm ${
                activeType === key ? 'bg-blue-600 text-white' : 'bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <table className="w-full text-sm">
          <thead className="text-gray-500">
            <tr>
              <th className="text-right p-2">دوره</th>
              <th className="text-right p-2">حجم</th>
              <th className="text-right p-2">آخرین تغییر</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {shown.map((f) => (
              <tr key={`${f.raster_type}-${f.period}`} className="border-t">
                <td className="p-2">{formatPeriod(f.period)}</td>
                <td className="p-2">{fmtSize(f.size_bytes)}</td>
                <td className="p-2">{formatJalaliDateTime(f.modified_at)}</td>
                <td className="p-2 text-left">
                  <button
                    type="button"
                    disabled={status.busy}
                    onClick={() => remove(f.raster_type, f.period)}
                    className="text-red-600 disabled:opacity-50"
                  >
                    حذف
                  </button>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan="4" className="p-3 text-gray-500">فایلی موجود نیست</td></tr>
            )}
          </tbody>
        </table>
      </section>

      {/* ---- change history ---------------------------------------------- */}
      <section className="bg-white rounded-lg shadow p-4">
        <h2 className="font-semibold mb-3">تاریخچه‌ی تغییرات</h2>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-gray-500 sticky top-0 bg-white">
              <tr>
                <th className="text-right p-2">زمان</th>
                <th className="text-right p-2">عملیات</th>
                <th className="text-right p-2">نوع</th>
                <th className="text-right p-2">دوره</th>
                <th className="text-right p-2">حجم</th>
                <th className="text-right p-2">کاربر</th>
              </tr>
            </thead>
            <tbody>
              {log.map((e) => (
                <tr key={e.log_id} className="border-t">
                  <td className="p-2">{formatJalaliDateTime(e.created_at)}</td>
                  <td className="p-2">{ACTION_LABEL[e.action] || e.action}</td>
                  <td className="p-2">{e.raster_type}</td>
                  <td className="p-2">{formatPeriod(e.period)}</td>
                  <td className="p-2">{fmtSize(e.size_bytes)}</td>
                  <td className="p-2">{e.performed_by_name || '—'}</td>
                </tr>
              ))}
              {log.length === 0 && (
                <tr><td colSpan="6" className="p-3 text-gray-500">هنوز تغییری ثبت نشده</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default RasterFiles;
